import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { ConfiguracionService } from '../../../services/configuracion.service';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';

@Component({
    selector: 'app-conf-consumos-new-edit',
    templateUrl: './new-edit.component.html'
})
export class ConfiguradorConsumosNewEditComponent implements OnInit, OnDestroy {

    private _configuradorConsumo;

    private readonly destroy$ = new Subject<void>();

    public newEditForm: UntypedFormGroup;
    public showError: boolean;
    public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
    public mensajes = new Array<Message>();

    public etiquetadorId;

    public almacenes;
    public denominaciones;
    public designaciones;
    public confecciones;
    public marcas;

    public disabledEtiquetadores: Subject<boolean> = new Subject();
    public disabledAlmacenes: Subject<boolean> = new Subject();
    public disabledDenominaciones: Subject<boolean> = new Subject();
    public disabledDesignaciones: Subject<boolean> = new Subject();
    public disabledConfecciones: Subject<boolean> = new Subject();
    public disabledMarcas: Subject<boolean> = new Subject();
    public disabledDestinos: Subject<boolean> = new Subject();

    private toCloseSpinner = 0;

    public columns = [this._translate.instant('global.finca'),
                      this._translate.instant('global.municipio'),
                      this._translate.instant('configurador-consumos.configurador.comboDesignacion.etiquetador'),
                      this._translate.instant('configurador-consumos.configurador.comboDesignacion.productor'),
                      this._translate.instant('configurador-consumos.configurador.comboDesignacion.marca'),
                      this._translate.instant('configurador-consumos.configurador.comboDesignacion.ggn'),
                      this._translate.instant('configurador-consumos.configurador.comboDesignacion.aforo')];

    public properties = ['des', 'desMunicipio', 'desEtiquetador', 'desProductor', 'desMarca', 'ggn', 'aforoCultivo'];


    @Input() configuradorConsumoId;

    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _dataProvider: DataProvider,
        private readonly _spinnerService: SpinnerService,
        private readonly _configuracionService: ConfiguracionService,
        private readonly _sessionContext: SessionContextService
    ) { }

    ngOnInit() {
        this.newEditForm = this._fb.group({
            etiquetador: ['', Validators.required],
            almacen: [{ value: '', disabled: true }, Validators.required],
            denominacion: [{ value: '', disabled: true }, Validators.required],
            designacion: [{ value: '', disabled: true }, Validators.required],
            confeccion: [{ value: '', disabled: true }, Validators.required],
            marca: [{ value: '', disabled: true }],
            destino: [{ value: '', disabled: false }],
            descripcion: [{ value: '', disabled: false }, Validators.required]
        });

        if (this.configuradorConsumoId) {
            this.getConfiguradorConsumo();
        }

        this.newEditForm.get('etiquetador').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((etiquetador) => {
            if (etiquetador) {
                this.getAlmacenesByEtiquetador(etiquetador.etiquetadorId);
                this.getDenominacionesByEtiquetador(etiquetador.etiquetadorId);
                this.getConfeccionesByEtiquetador(etiquetador.etiquetadorId);
                this.getMarcasByEtiquetador(etiquetador.etiquetadorId);
            } else {
                this.disabledAlmacenes.next(true);
                this.disabledDenominaciones.next(true);
                this.disabledConfecciones.next(true);
                this.disabledMarcas.next(true);
                this.almacenes = [];
                this.denominaciones = [];
                this.confecciones = [];
                this.marcas = [];
                this.newEditForm.get('almacen').patchValue('');
                this.newEditForm.get('almacen').disable();
                this.newEditForm.get('denominacion').patchValue('');
                this.newEditForm.get('denominacion').disable();
                this.newEditForm.get('confeccion').patchValue('');
                this.newEditForm.get('confeccion').disable();
                this.newEditForm.get('marca').patchValue('');
                this.newEditForm.get('marca').disable();
            }
        });

        this.newEditForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
            if (denominacion) {
                this.getDesignacionesByEtiquetadorDenominacion(this.etiquetadorId, denominacion.id);
            } else {
                this.disabledDesignaciones.next(true);
                this.designaciones = [];
                this.newEditForm.get('designacion').patchValue('');
                this.newEditForm.get('designacion').disable();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get etiquetadores() {
        return this._dataProvider.etiquetadoresPais;
    }

    public get destinos() {
        return this._dataProvider.destinos;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getAlmacenesByEtiquetador(etiquetadorId): Promise<any> {
        return new Promise((resolve, reject) => {
            let filters = {
                etiquetadorId: etiquetadorId
            };
            this.etiquetadorId = etiquetadorId;
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._configuracionService.getAlmacenesByEtiquetadorId(filters).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                if (resp.lista.length > 0) {
                    this.almacenes = resp.lista;
                    if (!this.configuradorConsumoId) {
                        this.disabledAlmacenes.next(false);
                        if (this.almacenes.length === 1) {
                            this.newEditForm.get('almacen').patchValue(this.almacenes[0]);
                            this.newEditForm.get('almacen').disable();
                            this.disabledAlmacenes.next(true);
                        }
                    }
                }
                setTimeout(resolve, 1);
            });
        });
    }

    public getDenominacionesByEtiquetador(etiquetadorId): Promise<any> {
        return new Promise((resolve, reject) => {
            let filters = {
                etiquetadorId: etiquetadorId
            };
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._configuracionService.getDenominacionesByEtiquetadorId(filters).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                if (resp.lista.length > 0) {
                    this.denominaciones = resp.lista;
                    if (!this.configuradorConsumoId) {
                        this.disabledDenominaciones.next(false);
                        if (this.denominaciones.length === 1) {
                            this.newEditForm.get('denominacion').patchValue(this.denominaciones[0]);
                            this.newEditForm.get('denominacion').disable();
                            this.disabledDenominaciones.next(true);
                        }
                    }
                }
                setTimeout(resolve, 1);
            });
        });
    }

    public getConfeccionesByEtiquetador(etiquetadorId): Promise<any> {
        return new Promise((resolve, reject) => {
            let filters = {
                etiquetadorId: etiquetadorId
            };
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._configuracionService.getConfeccionesByEtiquetadorId(filters).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                if (resp.lista.length > 0) {
                    this.confecciones = resp.lista;
                    if (!this.configuradorConsumoId) {
                        this.disabledConfecciones.next(false);
                        if (this.confecciones.length === 1) {
                            this.newEditForm.get('confeccion').patchValue(this.confecciones[0]);
                            this.newEditForm.get('confeccion').disable();
                            this.disabledConfecciones.next(true);
                        }
                    }
                }
                setTimeout(resolve, 1);
            });
        });
    }

    public getMarcasByEtiquetador(etiquetadorId): Promise<any> {
        return new Promise((resolve, reject) => {
            let filters = {
                etiquetadorId: etiquetadorId
            };
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._configuracionService.getMarcasByEtiquetadorId(filters).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                if (resp.lista.length > 0) {
                    this.marcas = resp.lista;
                    if (!this.configuradorConsumoId) {
                        this.disabledMarcas.next(false);
                    }
                } else {
                    this.newEditForm.get('marca').disable();
                    this.disabledMarcas.next(true);
                }
                setTimeout(resolve, 1);
            });
        });
    }

    public getDesignacionesByEtiquetadorDenominacion(etiquetadorId, denominacionId): Promise<any> {
        return new Promise((resolve, reject) => {
            let filters = {
                etiquetadorId: etiquetadorId,
                denominacionId: denominacionId
            };
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._configuracionService.getDesignacionesByEtiquetadorIdDenominacionId(filters).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                if (resp.lista.length > 0) {
                    this.designaciones = resp.lista;
                    if (!this.configuradorConsumoId) {
                        this.disabledDesignaciones.next(false);
                        if (this.designaciones.length === 1) {
                            this.newEditForm.get('designacion').patchValue(this.designaciones[0]);
                            this.newEditForm.get('designacion').disable();
                            this.disabledDesignaciones.next(true);
                        }
                    }
                }
                setTimeout(resolve, 1);
            });
        });
    }

    public getConfiguradorConsumo() {
        this._spinnerService.showSpinner();
        this._configuracionService.getConfiguradorConsumo(this.configuradorConsumoId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this._configuradorConsumo = resp;

            const promises: Promise<any>[] = [];
            promises.push(this.getAlmacenesByEtiquetador(resp.etiquetadorId));
            promises.push(this.getDenominacionesByEtiquetador(resp.etiquetadorId));
            promises.push(this.getConfeccionesByEtiquetador(resp.etiquetadorId));
            promises.push(this.getMarcasByEtiquetador(resp.etiquetadorId));
            promises.push(this.getDesignacionesByEtiquetadorDenominacion(resp.etiquetadorId, resp.denominacionId));
            Promise.all(promises).then(
                () => {
                    this.newEditForm.patchValue({
                        etiquetador: this.etiquetadores.find(x => x.etiquetadorId === resp.etiquetadorId),
                        almacen: this.almacenes.find(x => x.id === resp.almacenId),
                        denominacion: this.denominaciones.find(x => x.des === resp.desDenominacion),
                        confeccion: this.confecciones.find(x => x.id === resp.confeccionId),
                        designacion: this.designaciones.find(x => x.id === resp.designacionId),
                        destino: this.destinos.find(x => x.id === resp.destinoId),
                        marca: this.marcas ? this.marcas.find(x => x.id === resp.marcaId) : '',
                        descripcion: resp.des
                    });
                    this.newEditForm.disable();
                    this.disabledDestinos.next(true);
                    this.disabledMarcas.next(true);
                    this.disabledEtiquetadores.next(true);
                    this.disabledDenominaciones.next(true);
                }, (error) => {
                    console.log('Promise rejected with ' + JSON.stringify(error));
                }
            ).catch(e => console.log(e));
        }, (error) => {
            this.showApiError = true;
            if (error.status === 420) {
                this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
            } else {
                this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            }
        });
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    public save() {
        this.checkForm();
        if(this.newEditForm.valid && !this.configuradorConsumoId) {
            this._configuradorConsumo = {
                almacenId: this.newEditForm.get('almacen').value.id,
                designacionId: this.newEditForm.get('designacion').value.id,
                confeccionId: this.newEditForm.get('confeccion').value.id,
                etiquetadoComoMarcaId: this.newEditForm.get('marca').value ? this.newEditForm.get('marca').value.id : null,
                destinoId: this.newEditForm.get('destino').value ? this.newEditForm.get('destino').value.id : null,
                des: this.newEditForm.get('descripcion').value
            };
            this._spinnerService.showSpinner();
            this._configuracionService.createConfiguradorConsumo(this._configuradorConsumo).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                if (resp) {
                  this.refresh.emit(true);
                  this.mensaje.emit('global.success.itemCreated');
                } else {
                  this.showError = true;
                  this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            },
            (error) => {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            });

        }

    }

    public close() {
        this.refresh.emit(false);
    }

    private checkForm() {
        this.showError = this.newEditForm.invalid;
        if (this.showError) {
            this.mensajes.push({ class: 'error', message: 'global.error.required' });
        }
    }
}
